import React from 'react';
    import { motion } from 'framer-motion';
    import SafeIcon from '../common/SafeIcon';
    import { FiDroplet, FiLayout, FiCheckCircle, FiCopy, FiInfo, FiActivity } from 'react-icons/fi';

    const ColorBlock = ({ hex, name, description, isDark = true }) => {
      const copyToClipboard = () => {
        navigator.clipboard.writeText(hex);
        alert(`Hex code ${hex} copied to clipboard.`);
      };

      return (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card group"
        >
          <div 
            className="h-48 w-full relative flex items-end p-6" 
            style={{ backgroundColor: hex }}
          >
            <button 
              onClick={copyToClipboard}
              className={`absolute top-4 right-4 p-2 backdrop-blur-md rounded-full transition-all ${
                isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-black/10 text-black hover:bg-black/20'
              }`}
            >
              <SafeIcon icon={FiCopy} />
            </button>
            <span className={`font-black text-2xl tracking-tighter uppercase ${isDark ? 'text-white' : 'text-black'}`}>
              {hex}
            </span>
          </div>
          <div className="p-8">
            <h3 className="text-white font-black uppercase tracking-widest text-xs mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: hex }}></span>
              {name}
            </h3>
            <p className="text-slate-500 text-sm font-medium leading-relaxed">
              {description}
            </p>
          </div>
        </motion.div>
      );
    };

    const BrandGuidelines = () => {
      const coreColors = [
        { hex: '#CCFF00', name: 'Neon Lime', description: 'The primary brand signal. Used for high-priority actions, active states, and technical highlights.', isDark: false },
        { hex: '#050505', name: 'Midnight', description: 'Core application surface. Provides the high-contrast foundation for the digital news engine.', isDark: true },
        { hex: '#0A0A0A', name: 'Onyx', description: 'Layered surface color for cards and secondary containers. Creates depth against Midnight.', isDark: true },
        { hex: '#111111', name: 'Deep Grey', description: 'Input fields, code blocks, and deep section backgrounds. Used for interactive surfaces.', isDark: true }
      ];

      const UIColors = [
        { hex: '#FFFFFF', name: 'Pure White', description: 'Primary headline color. Ensures maximum readability for global news dispatch.', isDark: false },
        { hex: '#94A3B8', name: 'Slate 400', description: 'Standard body text color. Balanced for long-form reading on dark surfaces.', isDark: true },
        { hex: '#475569', name: 'Slate 600', description: 'Dimmed metadata and technical labels. Used for non-essential system information.', isDark: true },
        { hex: '#10B981', name: 'Live Emerald', description: 'Status indicator for live connections and active news pulses.', isDark: true }
      ];

      return (
        <div className="bg-[#050505] min-h-screen pt-44 pb-32 px-6 grid-pattern">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <header className="mb-24">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-[#CCFF00] flex items-center justify-center text-black">
                  <SafeIcon icon={FiDroplet} className="text-2xl" />
                </div>
                <div>
                  <h2 className="text-[#CCFF00] font-mono-data text-[11px] font-black uppercase tracking-[0.4em]">Visual Identity</h2>
                  <p className="text-white font-black uppercase tracking-tighter">Color Protocol v1.0.4</p>
                </div>
              </div>
              <h1 className="text-6xl md:text-8xl font-black text-white leading-[0.85] tracking-tighter mb-8 uppercase">
                The <span className="text-transparent" style={{ WebkitTextStroke: '1px #CCFF00' }}>Color</span> <br /> Palette
              </h1>
              <p className="text-slate-400 text-xl font-medium max-w-2xl leading-relaxed">
                A technical breakdown of the hexadecimal values and color logic used across The Pitch Feed agency's digital infrastructure.
              </p>
            </header>

            {/* Core Palette */}
            <section className="mb-32">
              <div className="flex items-center gap-4 mb-12">
                <h2 className="text-white font-black uppercase tracking-[0.3em] text-sm">Core Identity</h2>
                <div className="h-px flex-1 bg-white/10"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {coreColors.map((color) => (
                  <ColorBlock key={color.hex} {...color} />
                ))}
              </div>
            </section>

            {/* UI Palette */}
            <section className="mb-32">
              <div className="flex items-center gap-4 mb-12">
                <h2 className="text-white font-black uppercase tracking-[0.3em] text-sm">Functional Surfaces</h2>
                <div className="h-px flex-1 bg-white/10"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {UIColors.map((color) => (
                  <ColorBlock key={color.hex} {...color} />
                ))}
              </div>
            </section>

            {/* Contrast & Samples */}
            <section>
              <div className="flex items-center gap-4 mb-12">
                <h2 className="text-white font-black uppercase tracking-[0.3em] text-sm">Contrast Interaction</h2>
                <div className="h-px flex-1 bg-white/10"></div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div className="glass-card p-12">
                  <h4 className="text-[#CCFF00] font-mono-data text-[10px] uppercase tracking-widest mb-10">Typography Samples</h4>
                  <div className="space-y-10">
                    <div>
                      <h1 className="text-6xl font-black text-white uppercase tracking-tighter">Headline One</h1>
                      <p className="text-[10px] text-slate-600 font-mono-data uppercase mt-2">#FFFFFF on #050505</p>
                    </div>
                    <div>
                      <h2 className="text-4xl font-black text-[#CCFF00] uppercase tracking-tighter italic">Accent Headline</h2>
                      <p className="text-[10px] text-slate-600 font-mono-data uppercase mt-2">#CCFF00 on #050505</p>
                    </div>
                    <div>
                      <p className="text-xl text-slate-400 font-medium leading-relaxed">
                        The agency monitors global football feeds and delivers high-volume updates to professional sports media platforms.
                      </p>
                      <p className="text-[10px] text-slate-600 font-mono-data uppercase mt-2">#94A3B8 on #050505</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-8">
                  <div className="glass-card p-10 flex items-center justify-between border-l-4 border-l-[#CCFF00]">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#CCFF00] flex items-center justify-center text-black">
                        <SafeIcon icon={FiActivity} className="text-xl" />
                      </div>
                      <div>
                        <span className="block text-white font-black uppercase tracking-widest text-xs">Primary Signal</span>
                        <span className="text-[10px] text-slate-500 font-mono-data">Contrast Ratio: 4.5:1 (Neon Lime)</span>
                      </div>
                    </div>
                    <div className="w-6 h-6 rounded-full bg-[#CCFF00] shadow-[0_0_20px_rgba(204,255,0,0.4)]"></div>
                  </div>

                  <div className="glass-card p-10 flex items-center justify-between border-l-4 border-l-[#10B981]">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                      </div>
                      <div>
                        <span className="block text-white font-black uppercase tracking-widest text-xs">Live Status</span>
                        <span className="text-[10px] text-slate-500 font-mono-data">Functional Emerald</span>
                      </div>
                    </div>
                    <div className="w-6 h-6 rounded-full bg-emerald-500"></div>
                  </div>

                  <div className="bg-[#111] p-10 border border-white/5 flex flex-col gap-6">
                    <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Input Field Surface (#111111)</span>
                    <div className="w-full h-12 bg-black/50 border border-white/10"></div>
                    <button className="w-full py-4 bg-[#CCFF00] text-black font-black uppercase text-[10px] tracking-widest">
                      Action Button
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <footer className="mt-32 pt-20 border-t border-white/10 text-center">
              <p className="text-[10px] font-black text-slate-600 uppercase tracking-[0.5em] mb-4">End of Protocol</p>
              <div className="h-1 w-20 bg-[#CCFF00] mx-auto"></div>
            </footer>
          </div>
        </div>
      );
    };

    export default BrandGuidelines;